import React, { useEffect, useState } from 'react';
import * as brapiApi from '../api/brapi';

const number = value => Number.isFinite(Number(value)) ? Number(value) : null;
const pct = value => number(value) == null ? '—' : `${number(value) > 0 ? '+' : ''}${number(value).toFixed(2).replace('.', ',')}%`;
const pf = (row, net = false) => {
  const infinite = net ? row?.netProfitFactorInfinite : row?.profitFactorInfinite;
  const value = net ? row?.netProfitFactor : row?.profitFactor;
  return infinite ? '∞' : number(value) == null ? '—' : number(value).toFixed(2).replace('.', ',');
};
const stat = value => number(value) == null ? '—' : number(value).toFixed(2).replace('.', ',');
const labelPeriod = value => {
  const key = String(value || '');
  const regimeLabels = {
    'regime:2020': '2020',
    'regime:2021-2022': '2021–2022',
    'regime:2023-2024': '2023–2024',
    'regime:2025-to-date': '2025 até hoje',
    '2025-to-date': '2025 até hoje',
  };
  if (regimeLabels[key]) return regimeLabels[key];
  if (key.startsWith('year:')) return key.slice('year:'.length);
  return key ? key.replace('-', '–') : '—';
};
const isYearPeriod = value => String(value || '').startsWith('year:');
const diagnosticNumber = value => number(value);
const diagnosticMetric = (metrics, key) => {
  const value = diagnosticNumber(metrics?.[key]);
  return value == null ? '—' : value.toFixed(2).replace('.', ',');
};
const diagnosticPct = value => {
  const numeric = diagnosticNumber(value);
  return numeric == null ? '—' : `${numeric > 0 ? '+' : ''}${numeric.toFixed(2).replace('.', ',')}%`;
};

function EvidenceMetric({ label, value, tone = '' }) {
  return <div className="evidence-metric"><span>{label}</span><strong className={tone}>{value}</strong></div>;
}

function CostModel({ model }) {
  if (!model) return <div className="evidence-empty">Modelo de custos ainda não persistido.</div>;
  return (
    <div className="evidence-cost-model">
      <strong>Modelo usado</strong>
      <span>Emolumentos/taxa B3: {stat(model.exchangeFeeBpsPerSide)} bps por lado</span>
      <span>Corretagem: {stat(model.commissionBps)} bps por lado</span>
      <span>Slippage: {model.slippageFormula || 'modelo por ticker'}</span>
      <span>Spread: {model.spreadSource || '—'} · fallback {model.spreadFallbackPct ?? '—'}%</span>
      <span>Ativos no top 20: k={model.top20K ?? 1} · demais: k={model.restK ?? 1.5}</span>
    </div>
  );
}

function DiagnosticPanel({ data, state, error }) {
  const [sort, setSort] = useState({ key: 'pfBruto', direction: 'desc' });
  const rows = data?.rows || [];
  const selection = data?.selection;
  const sortedRows = [...rows].sort((left, right) => {
    const leftValue = diagnosticNumber(left.metrics?.[sort.key]);
    const rightValue = diagnosticNumber(right.metrics?.[sort.key]);
    if (leftValue == null && rightValue == null) return `${left.dimension}${left.bucket}`.localeCompare(`${right.dimension}${right.bucket}`);
    if (leftValue == null) return 1;
    if (rightValue == null) return -1;
    return sort.direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
  });
  const toggleSort = key => setSort(current => ({
    key,
    direction: current.key === key && current.direction === 'desc' ? 'asc' : 'desc',
  }));
  const sortLabel = (key, label) => (
    <button
      type="button"
      className="evidence-sort-button"
      onClick={() => toggleSort(key)}
      title={`Ordenar por ${label}`}
    >
      {label}{sort.key === key ? (sort.direction === 'asc' ? ' ↑' : ' ↓') : ''}
    </button>
  );
  const calibration = selection?.calibration;
  const test = selection?.test;
  const passes = selection?.passesAny === true;

  if (state === 'loading') return <div className="evidence-empty">Carregando diagnóstico 1b…</div>;
  if (state === 'error') return <div className="evidence-empty error">{error}</div>;
  if (!rows.length) return <div className="evidence-empty">O job de diagnóstico 1b ainda não foi executado.</div>;

  return (
    <div className="evidence-diagnostics">
      <div className={`diagnostic-selection ${passes ? 'passes' : 'fails'}`}>
        <strong>Critério 1b.8: {passes ? 'PASSA' : 'NÃO PASSA'}</strong>
        {selection?.available ? (
          <span>
            Seleção: {selection.variant} · {selection.regime} · calibração PF bruto {diagnosticMetric(calibration, 'pfBruto')}
            {' '}| N efetivo {diagnosticMetric(calibration, 'effectiveN')} | t-stat {diagnosticMetric(calibration, 'tStat')}
            {' '}| teste 2025-hoje PF bruto {diagnosticMetric(test, 'pfBruto')}
          </span>
        ) : (
          <span>{selection?.reason || 'Combinação selecionada indisponível.'}</span>
        )}
      </div>
      {data?.costModelV2 && (
        <div className="evidence-diagnostic-note">
          Cost model v2: {data.costModelV2.formula} · taxa B3 {data.costModelV2.exchangeFeePctPerSide}% por lado
          {' '}· k top 20={data.costModelV2.top20K} · k demais={data.costModelV2.restK}.
        </div>
      )}
      <div className="evidence-diagnostic-heading">
        <h4>Diagnóstico 1b · dimensões, variantes e validação temporal</h4>
        <span>Ordene por {sortLabel('pfBruto', 'PF bruto')}, {sortLabel('effectiveN', 'N efetivo')}
          {' '}ou {sortLabel('tStat', 't-stat')}</span>
      </div>
      <div className="evidence-table-wrap">
        <table className="evidence-table diagnostic-table">
          <thead>
            <tr>
              <th>Dimensão</th>
              <th>Bucket</th>
              <th>Trades</th>
              <th>{sortLabel('pfBruto', 'PF bruto')}</th>
              <th>PF líq v1</th>
              <th>PF líq v2</th>
              <th>Exp. bruta</th>
              <th>{sortLabel('effectiveN', 'N efetivo')}</th>
              <th>{sortLabel('tStat', 't-stat')}</th>
              <th>Teste PF bruto</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map(row => {
              const metrics = row.metrics || {};
              const highlight = metrics.highlight === true || metrics.passesCriterion === true;
              return (
                <tr key={`${row.dimension}-${row.bucket}`} className={highlight ? 'diagnostic-highlight' : ''}>
                  <td>{row.dimension}</td>
                  <td>{row.bucket}</td>
                  <td>{metrics.tradesTotal ?? '—'}</td>
                  <td>{diagnosticMetric(metrics, 'pfBruto')}</td>
                  <td>{diagnosticMetric(metrics, 'pfLiquidoV1')}</td>
                  <td>{diagnosticMetric(metrics, 'pfLiquidoV2')}</td>
                  <td className={diagnosticNumber(metrics.expectativaBrutaPct) >= 0 ? 'positive' : 'negative'}>
                    {diagnosticPct(metrics.expectativaBrutaPct)}
                  </td>
                  <td>{metrics.effectiveN ?? '—'}</td>
                  <td className={diagnosticNumber(metrics.tStat) >= 2 ? 'positive' : ''}>{diagnosticMetric(metrics, 'tStat')}</td>
                  <td>{diagnosticMetric(metrics, 'testPfBruto')}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {selection?.sizing && (
        <div className="evidence-diagnostic-note">
          Sizing: capital R$ {Number(selection.sizing.capital || 100000).toLocaleString('pt-BR')}
          {' '}· risco {selection.sizing.riskPct ?? 1}% por trade · máximo {selection.sizing.maxPositions ?? 8}
          {' '}posições · {selection.sizing.reason || 'retornos anuais calculados'}.
        </div>
      )}
    </div>
  );
}

export default function StrategyEvidencePanel({
  strategy,
  title = 'Evidência',
  strategyHealth = null,
}) {
  const [data, setData] = useState(null);
  const [state, setState] = useState('loading');
  const [error, setError] = useState(null);
  const [diagnosticData, setDiagnosticData] = useState(null);
  const [diagnosticState, setDiagnosticState] = useState('loading');
  const [diagnosticError, setDiagnosticError] = useState(null);
  const [activeTab, setActiveTab] = useState('evidence');
  const [periodView, setPeriodView] = useState('regime');

  useEffect(() => {
    let active = true;
    setState('loading');
    setError(null);
    const loadEvidence = brapiApi.getBacktestEvidence;
    if (typeof loadEvidence !== 'function') {
      setState('error');
      setError('Evidência indisponível nesta versão da API.');
      return () => { active = false; };
    }
    loadEvidence(strategy)
      .then(result => {
        if (!active) return;
        setData(result);
        setState('ready');
      })
      .catch(err => {
        if (!active) return;
        setError(err?.response?.data?.error || err.message || 'Evidência indisponível.');
        setState('error');
      });
    return () => { active = false; };
  }, [strategy]);

  useEffect(() => {
    let active = true;
    setDiagnosticState('loading');
    setDiagnosticError(null);
    const loadDiagnostics = brapiApi.getBacktestDiagnostics;
    if (typeof loadDiagnostics !== 'function') {
      setDiagnosticState('error');
      setDiagnosticError('Diagnóstico indisponível nesta versão da API.');
      return () => { active = false; };
    }
    loadDiagnostics(strategy)
      .then(result => {
        if (!active) return;
        setDiagnosticData(result);
        setDiagnosticState('ready');
      })
      .catch(err => {
        if (!active) return;
        setDiagnosticError(err?.response?.data?.error || err.message || 'Diagnóstico indisponível.');
        setDiagnosticState('error');
      });
    return () => { active = false; };
  }, [strategy]);

  const backtest = data?.backtest;
  const periods = data?.periods || [];
  const sensitivity = data?.sensitivity || [];
  const walkForward = data?.walkForward || [];
  const candidateV2 = data?.candidateV2 || data?.reversalV2 || data?.variants?.v2 || data?.candidate;
  const candidateV2Approved = candidateV2?.health?.blocked === false
    || candidateV2?.approved === true
    || data?.candidateV2Health?.blocked === false;
  const candidateReason = candidateV2?.health?.reason
    || candidateV2?.reason
    || data?.candidateV2Health?.reason
    || 'Evidência insuficiente para promoção.';
  const visiblePeriods = periods.filter(row => periodView === 'year'
    ? isYearPeriod(row.periodKey)
    : !isYearPeriod(row.periodKey));
  const maxSensitivity = Math.max(0.01, ...sensitivity.map(row => Math.abs(number(row?.netExpectancyPct) || 0)));
  const candidateRows = [
    ['candidateIfr2', 'S1 · IFR2 curto'],
    ['candidateSetup91', 'S2 · Setup 9.1'],
    ['candidateInsideBar', 'S3 · Inside bar com regime'],
    ['candidatePullbackRs', 'S4 · Pullback EMA20 + força relativa'],
  ].map(([key, label]) => ({
    key,
    label,
    health: strategyHealth?.strategies?.[key] || null,
  }));

  return (
    <section className="rc-panel strategy-evidence" data-testid={`strategy-evidence-${strategy}`}>
      <div className="strategy-evidence-header">
        <div>
          <div className="rc-panel-title">{title}</div>
          <p>Resultados persistidos pelo mesmo snapshot que governa a automação.</p>
        </div>
        {state === 'loading' && <span className="evidence-state">Carregando…</span>}
        {state === 'error' && <span className="evidence-state error">Indisponível</span>}
      </div>
      {strategy === 'reversal' && candidateV2 && !candidateV2Approved && (
        <div className="evidence-candidate" role="status">
          <strong>Reversão v2 · candidata reprovada</strong>
          <span>
            {candidateReason} · {candidateV2.tradesTotal ?? '—'} trades ·
            {' '}PF líquido {pf(candidateV2, true)} ·
            {' '}expectância líquida {pct(candidateV2.netExpectancyPct)}
          </span>
        </div>
      )}
      <div className="evidence-tabs" role="tablist" aria-label="Conteúdo da evidência">
        <button type="button" role="tab" aria-selected={activeTab === 'evidence'} className={activeTab === 'evidence' ? 'active' : ''} onClick={() => setActiveTab('evidence')}>Evidência</button>
        <button type="button" role="tab" aria-selected={activeTab === 'candidates'} className={activeTab === 'candidates' ? 'active' : ''} onClick={() => setActiveTab('candidates')}>Candidatos</button>
        <button type="button" role="tab" aria-selected={activeTab === 'diagnostic'} className={activeTab === 'diagnostic' ? 'active' : ''} onClick={() => setActiveTab('diagnostic')}>Diagnóstico</button>
      </div>
      {activeTab === 'candidates' && (
        <div className="evidence-candidates">
          <div className="evidence-diagnostic-heading">
            <h4>Candidatos da Fase 1d</h4>
            <span>Todos permanecem bloqueados até o gate persistido aprovar o snapshot.</span>
          </div>
          <div className="evidence-table-wrap">
            <table className="evidence-table">
              <thead>
                <tr>
                  <th>Setup</th>
                  <th>Estado</th>
                  <th>Trades</th>
                  <th>PF pond. líquido</th>
                  <th>Exp. pond. líquida</th>
                  <th>N efetivo</th>
                  <th>t-stat</th>
                </tr>
              </thead>
              <tbody>
                {candidateRows.map(candidate => {
                  const health = candidate.health;
                  const approved = health?.available && health.blocked === false;
                  return (
                    <tr key={candidate.key}>
                      <td>{candidate.label}</td>
                      <td className={approved ? 'evidence-positive' : 'evidence-negative'}>
                        {approved ? 'Aprovado' : 'Bloqueado'}
                      </td>
                      <td>{health?.tradesTotal ?? '—'}</td>
                      <td>{health?.weightedNetProfitFactorInfinite ? '∞' : stat(health?.weightedNetProfitFactor)}</td>
                      <td>{pct(health?.weightedNetExpectancyPct)}</td>
                      <td>{health?.effectiveN ?? '—'}</td>
                      <td>{stat(health?.tStat)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {activeTab === 'diagnostic' && (
        <DiagnosticPanel data={diagnosticData} state={diagnosticState} error={diagnosticError} />
      )}
      {activeTab === 'evidence' && (
      <>
      {state === 'loading' && <div className="evidence-empty">Carregando períodos, custos e walk-forward…</div>}
      {state === 'error' && <div className="evidence-empty error">{error}</div>}
      {state === 'ready' && !periods.length && !sensitivity.length && !walkForward.length && (
        <div className="evidence-empty">O job de evidência ainda não foi executado.</div>
      )}
      {state === 'ready' && (periods.length || sensitivity.length || walkForward.length) > 0 && (
        <>
          <div className="evidence-metrics">
            <EvidenceMetric label="N efetivo" value={backtest?.effectiveN ?? '—'} />
            <EvidenceMetric label="t-stat" value={stat(backtest?.tStat)} tone={number(backtest?.tStat) >= 2 ? 'positive' : 'negative'} />
            <EvidenceMetric label="PF pond. líquido" value={backtest?.weightedNetProfitFactorInfinite ? '∞' : stat(backtest?.weightedNetProfitFactor)} tone={number(backtest?.weightedNetProfitFactor) >= 1 || backtest?.weightedNetProfitFactorInfinite ? 'positive' : 'negative'} />
            <EvidenceMetric label="Expectância pond. líquida (custo por ativo (modelo v1))" value={pct(backtest?.weightedNetExpectancyPct)} tone={number(backtest?.weightedNetExpectancyPct) >= 0 ? 'positive' : 'negative'} />
            <EvidenceMetric label="Trades" value={backtest?.tradesTotal ?? '—'} />
          </div>

          <div className="evidence-grid">
            <div className="evidence-block">
              <div className="evidence-period-header">
                <h4>Performance por período</h4>
                <div className="evidence-period-toggle" role="group" aria-label="Agrupamento dos períodos">
                  <button
                    type="button"
                    className={periodView === 'regime' ? 'active' : ''}
                    aria-pressed={periodView === 'regime'}
                    onClick={() => setPeriodView('regime')}
                  >
                    Regimes
                  </button>
                  <button
                    type="button"
                    className={periodView === 'year' ? 'active' : ''}
                    aria-pressed={periodView === 'year'}
                    onClick={() => setPeriodView('year')}
                  >
                    Ano-calendário
                  </button>
                </div>
              </div>
              <div className="evidence-table-wrap">
                <table className="evidence-table">
                   <thead><tr><th>Período</th><th>Trades</th><th>PF bruto</th><th>PF líquido</th><th>Expectância líquida</th><th>N efetivo</th><th>t-stat</th></tr></thead>
                  <tbody>
                    {visiblePeriods.map(row => (
                      <tr key={`${row.ticker || '__ALL__'}-${row.periodKey}`}>
                        <td>{labelPeriod(row.periodKey)}</td>
                        <td>{row.tradesTotal ?? '—'}</td>
                        <td>{pf(row)}</td>
                        <td>{pf(row, true)}</td>
                        <td className={number(row.netExpectancyPct) >= 0 ? 'positive' : 'negative'}>{pct(row.netExpectancyPct)}</td>
                        <td>{row.effectiveN ?? '—'}</td>
                        <td>{stat(row.tStat)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!visiblePeriods.length && (
                  <div className="evidence-empty">Não há períodos deste agrupamento no snapshot.</div>
                )}
              </div>
            </div>

            <div className="evidence-block">
              <h4>Sensibilidade do slippage · por lado</h4>
              <div className="evidence-sensitivity">
                {sensitivity.map(row => {
                  const value = number(row.netExpectancyPct) || 0;
                  return (
                    <div className="evidence-sensitivity-row" key={row.slippagePctPerSide}>
                      <span>{String(row.slippagePctPerSide).replace('.', ',')}%</span>
                      <div className="evidence-bar-track"><i className={value >= 0 ? 'positive' : 'negative'} style={{ width: `${Math.min(100, Math.max(3, Math.abs(value) / maxSensitivity * 100))}%` }} /></div>
                      <strong className={value >= 0 ? 'positive' : 'negative'}>{pct(value)}</strong>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="evidence-block">
            <h4>Walk-forward · calibração 12 meses / teste 6 meses</h4>
            {!walkForward.length ? <div className="evidence-empty">Sem janelas suficientes para esta estratégia.</div> : (
              <div className="evidence-table-wrap">
                <table className="evidence-table">
                   <thead><tr><th>Janela</th><th>Parâmetros escolhidos</th><th>Calibração · PF bruto</th><th>Calibração · PF líquido</th><th>Teste · PF bruto</th><th>Teste · PF líquido</th></tr></thead>
                  <tbody>{walkForward.map(row => {
                    const params = row.selectedParams || {};
                     return <tr key={row.foldKey}><td>{row.testStart || row.foldKey} → {row.testEnd || '—'}</td><td>RSI {params.rsiThreshold ?? '—'} · stop {params.stopAtrMultiplier ?? '—'} ATR · alvo {params.targetMultiple ?? '—'}R</td><td>{pf(row.calibration || {})}</td><td>{pf(row.calibration || {}, true)}</td><td>{pf(row.test || {})}</td><td>{pf(row.test || {}, true)}</td></tr>;
                  })}</tbody>
                </table>
              </div>
            )}
          </div>
          <CostModel model={data?.costModel || backtest?.costModel} />
        </>
      )}
      </>
      )}
    </section>
  );
}